import moment from 'moment-timezone';
import {RRule, Weekday} from 'rrule';
import type {
  DateOffsetApi,
  WeekdayOffsetApi,
  NodeSchedule,
  Repeat,
  NodeScheduleApi,
  SchedulingValidations,
  Offset,
  NodeFrequencyLimitSource,
  NodeFrequencyLimitRule,
  NodeFrequencyLimitPayload,
} from 'src/types/node-schedule';
import type {
  TranslationFunction,
  MakeKeyFunction,
} from 'src/types/language-support';
import {parseRruleToRepeat, repeatToRruleString} from 'src/utils/rrule';
import {
  getFrequencyDurationKeyForLimit,
  getFrequencyDurationOptions,
} from 'src/utils/automation-workflow';

import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';


export const TIME_HOUR_MAX = 720;

const TIME_FORMAT = 'HH:mm:ss';

export const VALIDATIONS_INITIAL_STATE = {
  staticDateValueEmpty: false,
  staticDateValueWrong: false,
  staticTimeValueEmpty: false,
  dateFieldEmpty: false,
  dateOffsetSignEmpty: false,
  dateOffsetCountEmpty: false,
  dateOffsetUnitEmpty: false,
  weekdayOffsetCountEmpty: false,
  weekdayOffsetDayOfWeekEmpty: false,
  weekdayOffsetSignEmpty: false,
  timeFieldEmpty: false,
  repeatUntilEmpty: false,
  repeatFreqEmpty: false,
  repeatFreqWrong: false,
  monthlyDayEmpty: false,
  bymonthdayEmpty: false,
  byweekdayEmpty: false,
  skipEmpty: false,
  skipWrong: false,
  weekEmpty: false,
  dateEmpty: false,
  dateWrong: false,
  occurrenceEmpty: false,
  occurrenceWrong: false,
  batchingStartDateEmpty: false,
  batchingStartDateWrong: false,
  batchingStartTimeEmpty: false,
  batchingEndTimeEmpty: false,
  batchingRuleEmpty: false,
  recurrenceIntervalEmpty: false,
  recurrenceIntervalWrong: false,
  batchingUntilDateEmpty: false,
  batchingUntilDateWrong: false,
  batchingOccurrenceEmpty: false,
  batchingOccurrenceWrong: false,
  batchingBatchSizeEmpty: false,
  batchingBatchSizeWrong: false,
} as const;

/*
 *  This method assumes that the hourInMinutes are any number of minutes up to 720 (12:00) and timePeriod is either
 *  0 (for AM) or 1 (for PM).
 *  It will return the hour as an integer between 0 and 23 (inclusive) which represents the hour in military time and
 *  the minute within the hour as an integer.
 *
 */
export function getMilitaryHourAndMinute(
  hourInMinutes: string,
  timePeriod: string,
): {
  militaryHour: number;
  minute: number;
} {
  const timeInMinutes =
    parseInt(hourInMinutes, 10) + parseInt(timePeriod, 10) * TIME_HOUR_MAX;
  const militaryHour = Math.floor(timeInMinutes / 60);
  const minute = timeInMinutes % 60;
  return {militaryHour, minute};
}

export function getHourPeriodFromTime(time: string): {
  hour: string;
  period: string;
} {
  let timeHour = '0',
    timePeriod = '0';

  if (time) {
    const hours = parseInt(time.slice(0, 2), 10);
    const minutes = hours * 60 + parseInt(time.slice(3, 5), 10);
    timeHour = (minutes % TIME_HOUR_MAX) + '';
    timePeriod = minutes >= TIME_HOUR_MAX ? '1' : '0';
  }

  return {hour: timeHour, period: timePeriod};
}

function getTimeFromHourAndMinute(
  hour: number,
  minute: number,
  timezone: undefined,
) {
  return (
    moment
      // @ts-expect-error - TS2345 - Argument of type 'undefined' is not assignable to parameter of type 'string'. | TS2769 - No overload matches this call.
      .tz(moment.tz(timezone).format('YYYY-MM-DD'), timezone)
      .hour(hour)
      .minute(minute)
      .format(TIME_FORMAT)
  );
}

export function getTimeFromHourPeriod(hour: string, period: string): string {
  const {militaryHour, minute} = getMilitaryHourAndMinute(hour, period);
  // @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
  return getTimeFromHourAndMinute(militaryHour, minute);
}

export function displaySchedulingDate(date: string, locale?: string): string {
  if (locale) {
    moment.locale(locale);
  }
  return moment(date).format('L'); // Uses locale-specific date format
}

export const TIME_HOUR_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  {
    value: '0',
    label: '12:00',
  },
  {
    value: '30',
    label: '12:30',
  },
  {
    value: '60',
    label: '1:00',
  },
  {
    value: '90',
    label: '1:30',
  },
  {
    value: '120',
    label: '2:00',
  },
  {
    value: '150',
    label: '2:30',
  },
  {
    value: '180',
    label: '3:00',
  },
  {
    value: '210',
    label: '3:30',
  },
  {
    value: '240',
    label: '4:00',
  },
  {
    value: '270',
    label: '4:30',
  },
  {
    value: '300',
    label: '5:00',
  },
  {
    value: '330',
    label: '5:30',
  },
  {
    value: '360',
    label: '6:00',
  },
  {
    value: '390',
    label: '6:30',
  },
  {
    value: '420',
    label: '7:00',
  },
  {
    value: '450',
    label: '7:30',
  },
  {
    value: '480',
    label: '8:00',
  },
  {
    value: '510',
    label: '8:30',
  },
  {
    value: '540',
    label: '9:00',
  },
  {
    value: '570',
    label: '9:30',
  },
  {
    value: '600',
    label: '10:00',
  },
  {
    value: '630',
    label: '10:30',
  },
  {
    value: '660',
    label: '11:00',
  },
  {
    value: '690',
    label: '11:30',
  },
];

export const BATCHING_TIME_HOUR_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  {
    value: '0',
    label: '12:00',
  },
  {
    value: '60',
    label: '1:00',
  },
  {
    value: '120',
    label: '2:00',
  },
  {
    value: '180',
    label: '3:00',
  },
  {
    value: '240',
    label: '4:00',
  },
  {
    value: '300',
    label: '5:00',
  },
  {
    value: '360',
    label: '6:00',
  },
  {
    value: '420',
    label: '7:00',
  },
  {
    value: '480',
    label: '8:00',
  },
  {
    value: '540',
    label: '9:00',
  },
  {
    value: '600',
    label: '10:00',
  },
  {
    value: '660',
    label: '11:00',
  },
];

export const TIME_PERIOD_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  {
    value: '0',
    label: 'AM',
  },
  {
    value: '1',
    label: 'PM',
  },
];

export const SCHEDULE_DATE_TYPES = {
  TRIGGER_DATE: 'TODAY',
  STATIC_DATE: 'STATIC',
  DATE_FIELD: 'DYNAMIC',
  BATCHING: 'BATCHING',
} as const;

export const SCHEDULE_TIME_TYPES = {
  TRIGGER_TIME: 'NOW',
  STATIC_TIME: 'STATIC',
  TIME_FIELD: 'DYNAMIC',
  BATCHING: 'BATCHING',
} as const;

/** Per-node frequency limit UI / normalization keys */
export const NODE_FREQUENCY_LIMIT_SOURCE = {
  INHERIT: 'inherit',
  CUSTOM: 'custom',
} as const;

export const NODE_FREQUENCY_LIMIT_RULE = {
  NO_RESTRICT: 'no_restrict',
  RESTRICT_ONCE: 'restrict_once',
  AFTER_DAYS: 'after_days',
} as const;

/** Derive panel UI state from stored/API `frequencyLimit` (null = inherit). */
export function deriveNodeFrequencyLimitUi(
  frequencyLimit: NodeFrequencyLimitPayload | null | undefined,
): {
  source: NodeFrequencyLimitSource;
  rule: NodeFrequencyLimitRule;
  /** Key for workflow-style duration presets (`FREQUENCY_DURATION_OPTIONS`) or `DAY:<n>`. */
  durationOptionKey: string;
} {
  if (frequencyLimit == null) {
    return {
      source: NODE_FREQUENCY_LIMIT_SOURCE.INHERIT,
      rule: NODE_FREQUENCY_LIMIT_RULE.AFTER_DAYS,
      durationOptionKey: '1_DAY',
    };
  }
  const durType = String(frequencyLimit.duration?.type ?? '').toUpperCase();
  if (frequencyLimit.count === -1) {
    return {
      source: NODE_FREQUENCY_LIMIT_SOURCE.CUSTOM,
      rule: NODE_FREQUENCY_LIMIT_RULE.NO_RESTRICT,
      durationOptionKey: '1_DAY',
    };
  }
  if (durType === 'FOREVER') {
    return {
      source: NODE_FREQUENCY_LIMIT_SOURCE.CUSTOM,
      rule: NODE_FREQUENCY_LIMIT_RULE.RESTRICT_ONCE,
      durationOptionKey: '1_DAY',
    };
  }
  return {
    source: NODE_FREQUENCY_LIMIT_SOURCE.CUSTOM,
    rule: NODE_FREQUENCY_LIMIT_RULE.AFTER_DAYS,
    durationOptionKey: getFrequencyDurationKeyForLimit(frequencyLimit),
  };
}

/** Build node `frequencyLimit` from blackout-style duration option key. */
export function nodeFrequencyLimitFromDurationKey(
  key: string,
  t: (key: string, defaultValue: string) => string,
): NodeFrequencyLimitPayload {
  if (key.startsWith('DAY:')) {
    const days = Math.max(1, parseInt(key.slice(4), 10) || 1);
    return {count: 1, duration: {type: 'DAY', value: days}};
  }
  const opts = getFrequencyDurationOptions(t);
  const opt = opts[key];
  if (!opt) {
    return {count: 1, duration: {type: 'DAY', value: 1}};
  }
  const {duration} = opt;
  return {
    count: duration.count,
    duration: {type: duration.type, value: duration.value},
  };
}

export const getDateOffsetObj = (date_offset: DateOffsetApi): Offset => {
  if (!date_offset) {
    return null;
  }
  const {years = 0, months = 0, weeks = 0, days = 0} = date_offset || {};
  if (typeof years === 'number' && years !== 0) {
    return {
      unit: 'years',
      count: Math.abs(years),
      sign: years < 0 ? -1 : 1,
    };
  } else if (typeof months === 'number' && months !== 0) {
    return {
      unit: 'months',
      count: Math.abs(months),
      sign: months < 0 ? -1 : 1,
    };
  } else if (typeof weeks === 'number' && weeks !== 0) {
    return {
      unit: 'weeks',
      count: Math.abs(weeks),
      sign: weeks < 0 ? -1 : 1,
    };
  } else if (typeof days === 'number' && days !== 0) {
    return {
      unit: 'days',
      count: Math.abs(days) || 0,
      sign: !!days && days < 0 ? -1 : 1,
    };
  } else {
    return null;
  }
};

export const getWeekdayOffsetObj = (
  weekday_offset: WeekdayOffsetApi,
): Offset | null => {
  if (!weekday_offset) {
    return null;
  }
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  const {day_of_week, offset = 0} = weekday_offset || {};
  if (!day_of_week || !offset) {
    return null;
  }
  return {
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    day_of_week,
    count: Math.abs(offset) || 0,
    sign: offset < 0 ? -1 : 1,
  };
};

// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
const getTimeOffsetObj = (time_offset) => {
  const {hours = 0} = time_offset || {};
  if (!!hours && (hours > 0 || hours < 0)) {
    return {
      unit: 'hours',
      count: Math.abs(hours) || 0,
      sign: !!hours && hours < 0 ? -1 : 1,
    };
  } else {
    return null;
  }
};

export const parseNodeSchedule = (payload: NodeScheduleApi): NodeSchedule => {
  const {
    date_type,
    date_value,
    time_type,
    time_value,
    date_offset,
    weekday_offset,
    recurring_rule,
    blackout_action,
    batching_rule,
  } = payload;
  let dateValue = date_value;
  let timeValue = time_value;
  let dateField = null;
  let timeField = null;
  if (date_type === SCHEDULE_DATE_TYPES.DATE_FIELD) {
    dateField = date_value ?? null;
    dateValue = '';
  }
  if (time_type === SCHEDULE_TIME_TYPES.TIME_FIELD) {
    timeField = time_value ?? null;
    timeValue = '';
  }
  const dateOffset =
    date_type === SCHEDULE_DATE_TYPES.DATE_FIELD
      ? getDateOffsetObj(date_offset)
      : null;
  const weekdayOffset =
    date_type === SCHEDULE_DATE_TYPES.DATE_FIELD
      ? getWeekdayOffsetObj(weekday_offset)
      : null;
  const timeOffset =
    time_type === SCHEDULE_TIME_TYPES.TIME_FIELD
      ? getTimeOffsetObj(payload.time_offset)
      : null;

  let repeat: Repeat = {
    interval: 1,
    freq: RRule.DAILY,
  };
  if (recurring_rule && recurring_rule.rule) {
    const {rule} = recurring_rule;
    const options = RRule.parseString(rule);
    const {interval, freq, until, count, byweekday, bymonthday} = options;
    let repeatUntilKey = UNTIL_OPTIONS.INDEFINITELY;
    let monthlyDayKey = MONTHLY_DAY_OPTIONS.SAME_DAY;
    if (freq === RRule.MONTHLY && bymonthday) {
      // repeats on specific month days, bymonthday is an array of month days
      monthlyDayKey = MONTHLY_DAY_OPTIONS.SPECIFIC_MONTH_DAY;
    } else if (freq === RRule.MONTHLY && byweekday) {
      // repeats on specific week days, byweekday is an array of week days , but single value only.
      monthlyDayKey = MONTHLY_DAY_OPTIONS.SPECIFIC_WEEK_DAY;
    }
    // TODO Check is until is a date instance.
    let endDate = null;
    if (until) {
      // @ts-expect-error - TS2322 - Type '"1"' is not assignable to type '"0"'.
      repeatUntilKey = UNTIL_OPTIONS.SPECIFIC_DATE;
      endDate = moment(until).format('YYYY-MM-DD');
    } else if (count) {
      // @ts-expect-error - TS2322 - Type '"2"' is not assignable to type '"0"'.
      repeatUntilKey = UNTIL_OPTIONS.REPEAT_LIMIT;
    }

    // Convert byweekday to Weekday objects for consistent storage
    // RRule.parseString returns Weekday objects, so we can use them directly
    const normalizedByweekday: Weekday | Weekday[] | null | undefined =
      byweekday
        ? Array.isArray(byweekday)
          ? (byweekday as Weekday[])
          : (byweekday as Weekday)
        : null;

    repeat = {
      interval,
      freq,
      repeatUntilKey,
      // @ts-expect-error - TS2322 - Type 'number | null | undefined' is not assignable to type 'number | undefined'.
      count,
      until: endDate,
      monthlyDayKey,
      byweekday: normalizedByweekday,
      bymonthday,
    };
  }
  const rawFlPayload = payload.frequency_limit_settings;
  const frequencyLimit: NodeFrequencyLimitPayload | null =
    rawFlPayload != null
      ? {
          count: Number(rawFlPayload.count),
          duration: {
            type: String(rawFlPayload.duration?.type ?? 'FOREVER'),
            value:
              rawFlPayload.duration?.value != null
                ? Number(rawFlPayload.duration.value)
                : null,
          },
        }
      : null;

  // Parse rrule string to repeat object when receiving from API
  const batchingRule = batching_rule
    ? {
        rule: batching_rule.rule,
        repeat: batching_rule.rule
          ? parseRruleToRepeat(batching_rule.rule)
          : undefined,
        batch_size: batching_rule.batch_size,
        start_time: batching_rule.start_time,
        end_time: batching_rule.end_time,
      }
    : null;

  return {
    date_type,
    date_value: dateValue,
    date_field: dateField,
    weekdayOffset,
    dateOffset,
    time_type,
    time_value: timeValue,
    time_field: timeField,
    timeOffset,
    repeat_enable: !!payload.recurring_rule,
    repeat,
    skip: recurring_rule?.first_instance
      ? String(recurring_rule.first_instance - 1)
      : '0',
    blackout_action,
    frequencyLimit,
    batchingRule,
  };
};

export const repeatUntilOptions = (
  t: (key: string, defaultValue: string) => string,
) => [
  {
    key: UNTIL_OPTIONS.INDEFINITELY,
    label: t('Indefinitely', 'Indefinitely'),
  },
  {
    key: UNTIL_OPTIONS.SPECIFIC_DATE,
    label: t('Specific_Date', 'Specific Date'),
  },
  {
    key: UNTIL_OPTIONS.REPEAT_LIMIT,
    label: t('Repeat_Limit', 'Repeat Limit'),
  },
];

export const parseRepeatUntil = (
  until: string,
  count: number,
): {repeatUntilKey: string; endDate: string | null} => {
  let repeatUntilKey = UNTIL_OPTIONS.INDEFINITELY;
  let endDate = null;
  if (until) {
    // @ts-expect-error - TS2322 - Type '"1"' is not assignable to type '"0"'.
    repeatUntilKey = UNTIL_OPTIONS.SPECIFIC_DATE;
    endDate = moment(until).format('YYYY-MM-DD');
  } else if (count) {
    // @ts-expect-error - TS2322 - Type '"2"' is not assignable to type '"0"'.
    repeatUntilKey = UNTIL_OPTIONS.REPEAT_LIMIT;
  }
  return {repeatUntilKey, endDate};
};

export const getScheduleSummaryText = (
  {
    date_type,
    date_value,
    date_field,
    time_type,
    time_value,
    time_field,
    batchingRule,
  }: NodeSchedule,
  scheduleSeenOnce: boolean,
): string => {
  let schedulingSummary = 'Send';
  if (scheduleSeenOnce) {
    return `${schedulingSummary} when the previous node is completed`;
  }
  if (date_type === SCHEDULE_DATE_TYPES.TRIGGER_DATE) {
    schedulingSummary = `${schedulingSummary} when workflow triggers`;
  } else if (date_type === SCHEDULE_DATE_TYPES.STATIC_DATE && date_value) {
    schedulingSummary = `${schedulingSummary} on ${date_value}`;
  } else if (date_type === SCHEDULE_DATE_TYPES.DATE_FIELD && date_field) {
    schedulingSummary = `${schedulingSummary} on ${date_field}`;
  } else if (date_type === SCHEDULE_DATE_TYPES.BATCHING && batchingRule) {
    // Handle batching summary
    if (date_value) {
      schedulingSummary = `${schedulingSummary} batching starting on ${date_value}`;
    } else {
      schedulingSummary = `${schedulingSummary} batching`;
    }

    // Add start time and end time
    if (batchingRule.start_time && batchingRule.end_time) {
      const {hour: startTimeHour, period: startTimePeriod} =
        getHourPeriodFromTime(batchingRule.start_time);
      const {hour: endTimeHour, period: endTimePeriod} = getHourPeriodFromTime(
        batchingRule.end_time,
      );
      const startHour = TIME_HOUR_OPTIONS.find(
        (option) => option.value === startTimeHour,
      );
      const startPeriod = TIME_PERIOD_OPTIONS.find(
        (option) => option.value === startTimePeriod,
      );
      const endHour = TIME_HOUR_OPTIONS.find(
        (option) => option.value === endTimeHour,
      );
      const endPeriod = TIME_PERIOD_OPTIONS.find(
        (option) => option.value === endTimePeriod,
      );

      if (startHour && startPeriod && endHour && endPeriod) {
        schedulingSummary = `${schedulingSummary} from ${startHour.label} ${startPeriod.label} to ${endHour.label} ${endPeriod.label}`;
      }
    }

    // Add batch size
    if (batchingRule.batch_size) {
      schedulingSummary = `${schedulingSummary}, ${batchingRule.batch_size} records/hour`;
    }

    // Add recurrence rule
    if (batchingRule.repeat) {
      const rruleText = rruleToText(batchingRule.repeat);
      if (rruleText) {
        schedulingSummary = `${schedulingSummary}, ${rruleText}`;
      }
    } else if (batchingRule.rule) {
      const rruleText = recurrenceRRuleToText(batchingRule.rule);
      if (rruleText) {
        schedulingSummary = `${schedulingSummary}, ${rruleText}`;
      }
    }
    return schedulingSummary;
  }

  if (time_type === SCHEDULE_TIME_TYPES.TRIGGER_TIME) {
    schedulingSummary = `${schedulingSummary} at the scheduled time`;
  } else if (time_type === SCHEDULE_TIME_TYPES.STATIC_TIME && time_value) {
    const {hour: timeHour, period: timePeriod} =
      getHourPeriodFromTime(time_value);
    const selectedHour = TIME_HOUR_OPTIONS.find(
      (option) => option.value === timeHour,
    );
    const selectedPeriod = TIME_PERIOD_OPTIONS.find(
      (option) => option.value === timePeriod,
    );
    if (selectedHour && selectedPeriod) {
      schedulingSummary = `${schedulingSummary} at ${selectedHour.label} ${selectedPeriod.label}`;
    }
  } else if (time_type === SCHEDULE_TIME_TYPES.TIME_FIELD && time_field) {
    schedulingSummary = `${schedulingSummary} at ${time_field}`;
  }
  return schedulingSummary;
};

export const UNTIL_OPTIONS = {
  INDEFINITELY: '0',
  SPECIFIC_DATE: '1',
  REPEAT_LIMIT: '2',
} as const;

export const MONTHLY_DAY_OPTIONS = {
  SAME_DAY: 'sameday',
  SPECIFIC_MONTH_DAY: 'bymonthday',
  SPECIFIC_WEEK_DAY: 'byweekday',
};

export const normalizeNodeSchedule = (
  schedulingStoreData: NodeSchedule,
): NodeScheduleApi => {
  const {
    date_type,
    date_value,
    time_type,
    time_value,
    repeat_enable,
    repeat,
    date_field,
    dateOffset,
    weekdayOffset,
    time_field,
    timeOffset,
    skip,
    blackout_action,
    batchingRule,
    frequencyLimit,
  } = schedulingStoreData;

  let frequency_limit_settings: NodeFrequencyLimitPayload | null = null;
  if (frequencyLimit != null && typeof frequencyLimit === 'object') {
    if (frequencyLimit.count === -1) {
      frequency_limit_settings = {
        count: -1,
        duration: {
          type: 'FOREVER',
          value:
            frequencyLimit.duration?.value !== undefined &&
            frequencyLimit.duration?.value !== null
              ? Number(frequencyLimit.duration.value)
              : 0,
        },
      };
    } else {
      const durType = String(
        frequencyLimit.duration?.type ?? 'FOREVER',
      ).toUpperCase();
      const isDay = durType === 'DAY';
      const isWeek = durType === 'WEEK';
      frequency_limit_settings = {
        count: frequencyLimit.count,
        duration: {
          type: isDay ? 'DAY' : isWeek ? 'WEEK' : 'FOREVER',
          value:
            isDay || isWeek
              ? Math.max(1, Number(frequencyLimit.duration?.value) || 1)
              : frequencyLimit.duration?.value !== undefined &&
                frequencyLimit.duration?.value !== null
              ? Number(frequencyLimit.duration.value)
              : 0,
        },
      };
    }
  }

  let date_offset = null;
  let weekday_offset = null;
  let dateValue = date_value;
  let timeValue = time_value;
  if (date_type === SCHEDULE_DATE_TYPES.DATE_FIELD) {
    if (dateOffset?.unit) {
      date_offset = {
        [dateOffset.unit]:
          // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string'. | TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string'.
          parseInt(dateOffset.count, 10) * parseInt(dateOffset.sign, 10),
      };
    }
    dateValue = date_field ?? '';
    if (weekdayOffset?.count) {
      weekday_offset = {
        day_of_week: weekdayOffset.day_of_week,
        // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string'.
        offset:
          // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string'.
          parseInt(weekdayOffset.count, 10) * parseInt(weekdayOffset.sign, 10),
      };
    }
  }
  let time_offset = null;
  if (time_type === SCHEDULE_TIME_TYPES.TIME_FIELD) {
    if (timeOffset?.count) {
      time_offset = {
        // @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'string'. | TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string'.
        hours: parseInt(timeOffset.count, 10) * parseInt(timeOffset.sign, 10),
      };
    }
    timeValue = time_field ?? '';
  }
  const rruleOptions = repeat ? normalizeRruleOptions(repeat) : {};
  const recurring_rule = repeat_enable
    ? {
        rule: RRule.optionsToString(rruleOptions),
        first_instance: Number(skip) + 1,
      }
    : null;

  // Convert repeat object to rrule string if repeat exists, otherwise use existing rule
  const batching_rule = batchingRule
    ? {
        rule: batchingRule.repeat
          ? repeatToRruleString(batchingRule.repeat)
          : batchingRule.rule,
        batch_size: batchingRule.batch_size,
        start_time: batchingRule.start_time,
        end_time: batchingRule.end_time,
      }
    : null;

  return {
    date_type,
    date_value: dateValue,
    time_type,
    date_offset,
    weekday_offset,
    time_value: timeValue,
    time_offset,
    recurring_rule,
    blackout_action,
    frequency_limit_settings,
    batching_rule,
  };
};

const normalizeRruleOptions = (repeatOptions: Repeat) => {
  const {
    freq = RRule.DAILY,
    count = 0,
    byweekday = [],
    interval = 0,
    until = '',
    repeatUntilKey,
    monthlyDayKey,
    bymonthday = null,
  } = repeatOptions || {};

  const rruleOptions: Record<string, any> = {freq, interval};
  if (repeatUntilKey === UNTIL_OPTIONS.SPECIFIC_DATE && until) {
    rruleOptions.until = new Date(until);
  } else if (repeatUntilKey === UNTIL_OPTIONS.REPEAT_LIMIT && count) {
    rruleOptions.count = count;
  }
  if (freq === RRule.WEEKLY && byweekday) {
    // For weekly recurrence, byweekday is stored as Weekday objects in UI
    // Extract .weekday to get numbers for RRule
    const weekdayArray = Array.isArray(byweekday) ? byweekday : [byweekday];
    const numbers = weekdayArray.map((wday) => (wday as Weekday).weekday);
    if (numbers.length > 0) {
      rruleOptions.byweekday = numbers.length === 1 ? numbers[0] : numbers;
    }
  }
  if (
    freq === RRule.MONTHLY &&
    monthlyDayKey === MONTHLY_DAY_OPTIONS.SPECIFIC_MONTH_DAY
  ) {
    rruleOptions.bymonthday = bymonthday;
  }
  if (
    freq === RRule.MONTHLY &&
    monthlyDayKey === MONTHLY_DAY_OPTIONS.SPECIFIC_WEEK_DAY &&
    byweekday
  ) {
    // For monthly recurrence, byweekday is stored as Weekday objects
    // RRule accepts Weekday objects directly, so we can use them as-is
    rruleOptions.byweekday = Array.isArray(byweekday) ? byweekday : [byweekday];
  }
  return rruleOptions;
};

export const rruleToText = (repeatOptions: Repeat): string => {
  const rruleOptions = normalizeRruleOptions(repeatOptions);
  const rule = new RRule(rruleOptions);
  return rule.toText();
};

export const recurrenceRRuleToText = (rrule: string): string => {
  const rruleObj = RRule.fromString(rrule);
  return rruleObj.toText();
};

export const getErrors = (errors: SchedulingValidations): Array<string> => {
  const err: Array<string> = [];
  const hasEmptyList = [
    'staticDateValueEmpty',
    'staticTimeValueEmpty',
    'dateFieldEmpty',
    'timeFieldEmpty',
    'repeatUntilEmpty',
    'repeatFreqEmpty',
    'monthlyDayEmpty',
    'bymonthdayEmpty',
    'byweekdayEmpty',
    'skipEmpty',
    'weekEmpty',
    'dateEmpty',
    'occurrenceEmpty',
    'batchingStartDateEmpty',
    'batchingStartTimeEmpty',
    'batchingEndTimeEmpty',
    'batchingRuleEmpty',
    'recurrenceIntervalEmpty',
    'batchingUntilDateEmpty',
    'batchingOccurrenceEmpty',
    'batchingBatchSizeEmpty',
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'SchedulingValidations'.
  ].some((err) => errors[err]);
  if (hasEmptyList) {
    err.push('Please fill the required fields.');
  }
  if (
    errors.dateOffsetSignEmpty ||
    errors.dateOffsetCountEmpty ||
    errors.dateOffsetUnitEmpty
  ) {
    err.push('Please fill the date offset fields.');
  }
  if (
    errors.weekdayOffsetCountEmpty ||
    errors.weekdayOffsetDayOfWeekEmpty ||
    errors.weekdayOffsetSignEmpty
  ) {
    err.push('Please fill the weekday offset fields.');
  }
  if (errors.staticDateValueWrong) {
    err.push('The selected date cannot be earlier than today\'s date.');
  }
  if (errors.batchingStartDateWrong) {
    err.push('The selected date cannot be earlier than today\'s date.');
  }
  if (errors.batchingUntilDateWrong) {
    err.push('The selected date cannot be earlier than today\'s date.');
  }
  if (errors.dateWrong) {
    err.push('The selected date cannot be earlier than today\'s date.');
  }
  if (errors.repeatFreqWrong) {
    err.push(
      'Please input a valid format for Repeat Every (Zero, negative numbers, and decimals are not permitted).',
    );
  }
  if (errors.occurrenceWrong) {
    err.push(
      'Please input a valid format for Occurrence (Zero, negative numbers, and decimals are not permitted).',
    );
  }
  if (errors.skipWrong) {
    err.push(
      'Please input a valid format for Skip (Negative numbers and decimals are not permitted).',
    );
  }
  if (errors.batchingBatchSizeWrong) {
    err.push(
      'Please input a valid format for Batch Size (Zero, negative numbers, and decimals are not permitted).',
    );
  }
  if (errors.recurrenceIntervalWrong) {
    err.push(
      'Please input a valid format for Process one batch for every (Zero, negative numbers, and decimals are not permitted).',
    );
  }
  if (errors.batchingOccurrenceWrong) {
    err.push(
      'Please input a valid format for Occurrence (Zero, negative numbers, and decimals are not permitted).',
    );
  }
  return err;
};
export const getDefaultNodeScheduleState = (
  overrides: Partial<NodeSchedule>,
): NodeSchedule => {
  return {
    //below are scheduling fields
    date_type: SCHEDULE_DATE_TYPES.TRIGGER_DATE,
    date_value: 'today',
    time_type: SCHEDULE_TIME_TYPES.TRIGGER_TIME,
    time_value: 'now',
    repeat_enable: false,
    skip: '0',
    repeat: {
      interval: 1,
      freq: RRule.DAILY,
      until: 0,
      count: 1,
    },
    dateOffset: null,
    weekdayOffset: null,
    timeOffset: {
      sign: 1,
      count: 0,
      unit: 'hours',
    },

    date_field: null,
    time_field: null,
    blackout_action: 'DO_NOT_APPLY',
    batchingRule: null,
    frequencyLimit: null,
    ...overrides,
  };
};

// pick node schedule data - check if it picks from Store data or API data.
export const pickNodeScheduleData = (nodeData: NodeSchedule): NodeSchedule => {
  const {
    date_type,
    date_value,
    time_type,
    time_value,
    dateOffset,
    weekdayOffset,
    date_field,
    repeat_enable,
    time_field,
    timeOffset,
    repeat,
    skip,
    blackout_action,
    batchingRule,
    frequencyLimit,
  } = nodeData;

  return {
    date_type,
    date_value,
    time_type,
    time_value,
    dateOffset,
    weekdayOffset,
    date_field,
    repeat_enable,
    time_field,
    timeOffset,
    repeat,
    skip,
    blackout_action,
    batchingRule,
    frequencyLimit,
  };
};

export const getMonthDayOptions = (
  t: (key: string, defaultValue: string) => string,
): Array<MenuOption> => {
  return [
    {
      key: '1',
      label: t('1st', '1st'),
    },
    {
      key: '2',
      label: t('2nd', '2nd'),
    },
    {
      key: '3',
      label: t('3rd', '3rd'),
    },
    {
      key: '4',
      label: t('4th', '4th'),
    },
    {
      key: '5',
      label: t('5th', '5th'),
    },
    {
      key: '6',
      label: t('6th', '6th'),
    },
    {
      key: '7',
      label: t('7th', '7th'),
    },
    {
      key: '8',
      label: t('8th', '8th'),
    },
    {
      key: '9',
      label: t('9th', '9th'),
    },
    {
      key: '10',
      label: t('10th', '10th'),
    },
    {
      key: '11',
      label: t('11th', '11th'),
    },
    {
      key: '12',
      label: t('12th', '12th'),
    },
    {
      key: '13',
      label: t('13th', '13th'),
    },
    {
      key: '14',
      label: t('14th', '14th'),
    },
    {
      key: '15',
      label: t('15th', '15th'),
    },
    {
      key: '16',
      label: t('16th', '16th'),
    },
    {
      key: '17',
      label: t('17th', '17th'),
    },
    {
      key: '18',
      label: t('18th', '18th'),
    },
    {
      key: '19',
      label: t('19th', '19th'),
    },
    {
      key: '20',
      label: t('20th', '20th'),
    },
    {
      key: '21',
      label: t('21st', '21st'),
    },
    {
      key: '22',
      label: t('22nd', '22nd'),
    },
    {
      key: '23',
      label: t('23rd', '23rd'),
    },
    {
      key: '24',
      label: t('24th', '24th'),
    },
    {
      key: '25',
      label: t('25th', '25th'),
    },
    {
      key: '26',
      label: t('26th', '26th'),
    },
    {
      key: '27',
      label: t('27th', '27th'),
    },
    {
      key: '28',
      label: t('28th', '28th'),
    },
    {
      key: '29',
      label: t('29th', '29th'),
    },
    {
      key: '-1',
      label: t('Last', 'Last'),
    },
  ];
};
export const getMonthlyWeekDayOptions = (
  t: (key: string, defaultValue: string) => string,
): Array<Omit<MenuOption, 'key'> & {key: Weekday}> => {
  return [
    {
      key: RRule.MO.nth(1),
      label: `${t('1st', '1st')} ${t('Monday', 'Monday')}`,
    },
    {
      key: RRule.TU.nth(1),
      label: `${t('1st', '1st')} ${t('Tuesday', 'Tuesday')}`,
    },
    {
      key: RRule.WE.nth(1),
      label: `${t('1st', '1st')} ${t('Wednesday', 'Wednesday')}`,
    },
    {
      key: RRule.TH.nth(1),
      label: `${t('1st', '1st')} ${t('Thursday', 'Thursday')}`,
    },
    {
      key: RRule.FR.nth(1),
      label: `${t('1st', '1st')} ${t('Friday', 'Friday')}`,
    },
    {
      key: RRule.SA.nth(1),
      label: `${t('1st', '1st')} ${t('Saturday', 'Saturday')}`,
    },
    {
      key: RRule.SU.nth(1),
      label: `${t('1st', '1st')} ${t('Sunday', 'Sunday')}`,
    },
    // All 2nd week options
    {
      key: RRule.MO.nth(2),
      label: `${t('2nd', '2nd')} ${t('Monday', 'Monday')}`,
    },
    {
      key: RRule.TU.nth(2),
      label: `${t('2nd', '2nd')} ${t('Tuesday', 'Tuesday')}`,
    },
    {
      key: RRule.WE.nth(2),
      label: `${t('2nd', '2nd')} ${t('Wednesday', 'Wednesday')}`,
    },
    {
      key: RRule.TH.nth(2),
      label: `${t('2nd', '2nd')} ${t('Thursday', 'Thursday')}`,
    },
    {
      key: RRule.FR.nth(2),
      label: `${t('2nd', '2nd')} ${t('Friday', 'Friday')}`,
    },
    {
      key: RRule.SA.nth(2),
      label: `${t('2nd', '2nd')} ${t('Saturday', 'Saturday')}`,
    },
    {
      key: RRule.SU.nth(2),
      label: `${t('2nd', '2nd')} ${t('Sunday', 'Sunday')}`,
    },
    // All 3rd week options
    {
      key: RRule.MO.nth(3),
      label: `${t('3rd', '3rd')} ${t('Monday', 'Monday')}`,
    },
    {
      key: RRule.TU.nth(3),
      label: `${t('3rd', '3rd')} ${t('Tuesday', 'Tuesday')}`,
    },
    {
      key: RRule.WE.nth(3),
      label: `${t('3rd', '3rd')} ${t('Wednesday', 'Wednesday')}`,
    },
    {
      key: RRule.TH.nth(3),
      label: `${t('3rd', '3rd')} ${t('Thursday', 'Thursday')}`,
    },
    {
      key: RRule.FR.nth(3),
      label: `${t('3rd', '3rd')} ${t('Friday', 'Friday')}`,
    },
    {
      key: RRule.SA.nth(3),
      label: `${t('3rd', '3rd')} ${t('Saturday', 'Saturday')}`,
    },
    {
      key: RRule.SU.nth(3),
      label: `${t('3rd', '3rd')} ${t('Sunday', 'Sunday')}`,
    },
    // All 4th week options
    {
      key: RRule.MO.nth(4),
      label: `${t('4th', '4th')} ${t('Monday', 'Monday')}`,
    },
    {
      key: RRule.TU.nth(4),
      label: `${t('4th', '4th')} ${t('Tuesday', 'Tuesday')}`,
    },
    {
      key: RRule.WE.nth(4),
      label: `${t('4th', '4th')} ${t('Wednesday', 'Wednesday')}`,
    },
    {
      key: RRule.TH.nth(4),
      label: `${t('4th', '4th')} ${t('Thursday', 'Thursday')}`,
    },
    {
      key: RRule.FR.nth(4),
      label: `${t('4th', '4th')} ${t('Friday', 'Friday')}`,
    },
    {
      key: RRule.SA.nth(4),
      label: `${t('4th', '4th')} ${t('Saturday', 'Saturday')}`,
    },
    {
      key: RRule.SU.nth(4),
      label: `${t('4th', '4th')} ${t('Sunday', 'Sunday')}`,
    },
    // All last week options
    {
      key: RRule.MO.nth(-1),
      label: `${t('Last', 'Last')} ${t('Monday', 'Monday')}`,
    },
    {
      key: RRule.TU.nth(-1),
      label: `${t('Last', 'Last')} ${t('Tuesday', 'Tuesday')}`,
    },
    {
      key: RRule.WE.nth(-1),
      label: `${t('Last', 'Last')} ${t('Wednesday', 'Wednesday')}`,
    },
    {
      key: RRule.TH.nth(-1),
      label: `${t('Last', 'Last')} ${t('Thursday', 'Thursday')}`,
    },
    {
      key: RRule.FR.nth(-1),
      label: `${t('Last', 'Last')} ${t('Friday', 'Friday')}`,
    },
    {
      key: RRule.SA.nth(-1),
      label: `${t('Last', 'Last')} ${t('Saturday', 'Saturday')}`,
    },
    {
      key: RRule.SU.nth(-1),
      label: `${t('Last', 'Last')} ${t('Sunday', 'Sunday')}`,
    },
  ];
};

export const getWeekCountOptions = (
  t: (key: string, defaultValue: string) => string,
): Array<MenuOption> => {
  return [
    {
      key: '1',
      label: t('1st', '1st'),
    },
    {
      key: '2',
      label: t('2nd', '2nd'),
    },
    {
      key: '3',
      label: t('3rd', '3rd'),
    },
    {
      key: '4',
      label: t('4th', '4th'),
    },
  ];
};

export const getWeekDayOptions = (
  t: (key: string, defaultValue: string) => string,
): Array<MenuOption> => {
  return [
    {
      key: 'MONDAY',
      label: t('Monday', 'Monday'),
    },
    {
      key: 'TUESDAY',
      label: t('Tuesday', 'Tuesday'),
    },
    {
      key: 'WEDNESDAY',
      label: t('Wednesday', 'Wednesday'),
    },
    {
      key: 'THURSDAY',
      label: t('Thursday', 'Thursday'),
    },
    {
      key: 'FRIDAY',
      label: t('Friday', 'Friday'),
    },
    {
      key: 'SATURDAY',
      label: t('Saturday', 'Saturday'),
    },
    {
      key: 'SUNDAY',
      label: t('Sunday', 'Sunday'),
    },
  ];
};

// return in format: "04/29/2025, Mon at 10:00 AM"
export const displaySchedulingDateDayAndTime = (
  dateValue: string,
  t: TranslationFunction,
  makeKey: MakeKeyFunction,
  locale?: string,
): string => {
  // Set moment locale if provided
  if (locale) {
    moment.locale(locale);
  }

  const displayDate = moment(dateValue).format('L'); // Uses locale-specific date format
  const dayOfWeek = moment(dateValue).format('ddd');
  const displayTime = moment(dateValue).format('LT'); // Uses locale-specific time format

  // Translate day of week and "at" text
  const translatedDayOfWeek = t(makeKey(dayOfWeek), dayOfWeek);
  const translatedAt = t('at', 'at');

  return `${displayDate}, ${translatedDayOfWeek} ${translatedAt} ${displayTime}`;
};
